import { createSelector } from "@reduxjs/toolkit";

import { selectMultiImages } from "../../../../components/ImageUploader/images.slice";
import { FormId } from "../../../../components/enums";
import { RootState } from "../../../../store";
import { UIState } from "../../../../store/HOC/genericUI.slice";
import {
  generateAddress,
  generatePhone,
  mapRoleTypeToLabel,
} from "../../../../utilities/forms";
import { parseDataToDDMMYYYY } from "../../../../utilities/functions";

import { ComponentId as CircumstancesComponentId } from "./CircumstancesData/enums";
import { ComponentId as OtherPersonsComponentId } from "./OtherPersonsData/enums";
import { ComponentId as StatementsComponentId } from "./StatementsData/enums";
import {
  IncidentCircumstancesFormInputs,
  IncidentOtherPersonsFormInputs,
  IncidentStatementsFormInputs,
} from "./incident.slice";

export const selectIncidentCircumstancesData = (
  store: RootState
): IncidentCircumstancesFormInputs =>
  store.ocProperty.incident.data[FormId.INCIDENT_CIRCUMSTANCES];

export const selectIncidentStatementsData = (
  store: RootState
): IncidentStatementsFormInputs =>
  store.ocProperty.incident.data[FormId.INCIDENT_STATEMENTS];

export const selectIsPoliceStatementSelected = createSelector(
  selectIncidentStatementsData,
  (
    statementsData: Pick<
      IncidentStatementsFormInputs,
      StatementsComponentId.TYPE
    >
  ) => statementsData[StatementsComponentId.TYPE] === "police"
);

export const selectIsPerpetratorStatementSelected = createSelector(
  selectIncidentStatementsData,
  (
    statementsData: Pick<
      IncidentStatementsFormInputs,
      StatementsComponentId.TYPE
    >
  ) => statementsData[StatementsComponentId.TYPE] === "perpetrator"
);

export const selectIncidentOtherPersonsData = (
  store: RootState
): IncidentOtherPersonsFormInputs =>
  store.ocProperty.incident.data[FormId.INCIDENT_OTHER_PERSONS];

export const selectIncidentOtherPersonsParticipantsData = (
  store: RootState
): Partial<IncidentOtherPersonsFormInputs> => ({
  [OtherPersonsComponentId.PERSONS]:
    store.ocProperty.incident.data[FormId.INCIDENT_OTHER_PERSONS][
      OtherPersonsComponentId.PERSONS
    ],
});

export const selectShouldDisplayOtherPersons = createSelector(
  selectIncidentOtherPersonsData,
  (otherPersonsData) =>
    otherPersonsData[OtherPersonsComponentId.ARE_OTHER_PEOPLE_INVOLVED] ===
    "true"
);

const selectIncidentDateTime = createSelector(
  selectIncidentCircumstancesData,
  (data) => ({
    name: "Data i godzina",
    value: [
      parseDataToDDMMYYYY(data[CircumstancesComponentId.DATE]),
      data[CircumstancesComponentId.HOUR],
    ].join(", "),
  })
);

const selectIncidentPlace = createSelector(
  selectIncidentCircumstancesData,
  (data) => ({
    name: "Miejsce",
    value: data[CircumstancesComponentId.PLACE],
  })
);

const selectIncidentAddress = createSelector(
  selectIncidentCircumstancesData,
  (data) => ({
    name: "Miejscowość, województwo, kraj",
    value: generateAddress(data),
  })
);

const selectIncidentCircumstancesDescription = createSelector(
  selectIncidentCircumstancesData,
  (data) => ({
    name: "Opis zdarzenia",
    value: data[CircumstancesComponentId.DESCRIPTION],
  })
);

const selectPoliceDepartment = createSelector(
  selectIncidentStatementsData,
  (data) => ({
    name: "Jednostka policji",
    value: data[StatementsComponentId.POLICE_DEPARTMENT],
  })
);

const selectCaseNumber = createSelector(
  selectIncidentStatementsData,
  (data) => ({
    name: "Numer sprawy",
    value: data[StatementsComponentId.CASE_NUMBER],
  })
);

const selectPoliceImages = createSelector(
  (data: RootState) =>
    selectMultiImages(data, {
      section: StatementsComponentId.POLICE_IMAGES,
    }),
  (data) => ({
    name: "Protokół policji",
    value: Object.values(data).map((image) => image.src as string),
  })
);

const selectIncidentPoliceStatements = createSelector(
  selectPoliceDepartment,
  selectCaseNumber,
  selectPoliceImages,
  (policeDepartment, caseNumber, images) => [
    policeDepartment,
    caseNumber,
    images,
  ]
);

const selectPerpetratorStatementsImages = createSelector(
  (data: RootState) =>
    selectMultiImages(data, {
      section: StatementsComponentId.PERPETRATOR_IMAGES,
    }),
  (data) => {
    return [
      {
        name: "Oświadczenia sprawcy",
        value: Object.values(data).map((image) => image.src as string),
      },
    ];
  }
);

const selectState = (state: RootState) => state;

const selectIncidentStatements = createSelector(
  selectIsPoliceStatementSelected,
  selectState,
  (isPoliceStatement: boolean, state: RootState) => [
    {
      name: "Typ dokumentu",
      value: isPoliceStatement ? "protokół policji" : "oświadczenie sprawcy",
    },
    ...(isPoliceStatement
      ? selectIncidentPoliceStatements(state)
      : selectPerpetratorStatementsImages(state)),
  ]
);

const selectIncidentCircumstancesSummaryData = createSelector(
  selectIncidentDateTime,
  selectIncidentPlace,
  selectIncidentAddress,
  selectIncidentCircumstancesDescription,
  selectIncidentStatements,
  (dateTime, place, address, description, statements) => [
    dateTime,
    place,
    address,
    description,
    ...statements,
  ]
);

const selectOtherPersonsData = createSelector(
  selectIncidentOtherPersonsData,
  (data) =>
    data.persons !== undefined
      ? Object.values(data.persons).map((person, index) => ({
          name: `Osoba ${index + 1}`,
          value: [
            [person.name, person.surname].join(" ").trim(),
            ["rola:", mapRoleTypeToLabel[person.role]].join(" "),
            generatePhone(person.phone),
            person.email,
            [
              [person.street, person.street2].join(" "),
              [person.zipCode, person.city].join(" "),
            ]
              .filter((item) => item.trim() !== "")
              .join(", "),
          ]
            .filter((item) => item.trim() !== "")
            .join("\r\n"),
        }))
      : []
);

const selectPersons = createSelector(
  selectShouldDisplayOtherPersons,
  selectState,
  (gotOtherPersonsData: boolean, state: RootState) => [
    {
      name: "Czy w zdarzeniu brały udział inne osoby?",
      value: gotOtherPersonsData ? "tak" : "nie",
    },
    ...(gotOtherPersonsData ? selectOtherPersonsData(state) : []),
  ]
);

const selectIncidentOtherPersonsSummaryData = createSelector(
  selectPersons,
  (persons) => [...persons]
);

export const selectSummaryData = createSelector(
  selectIncidentCircumstancesSummaryData,
  selectIncidentOtherPersonsSummaryData,
  (circumstances, otherPersons) => ({ circumstances, otherPersons })
);

export const selectFormData = createSelector(
  selectIncidentCircumstancesData,
  selectIncidentStatementsData,
  selectIncidentOtherPersonsData,
  (circumstances, statements, otherPersonsData) => {
    const { [OtherPersonsComponentId.PERSONS]: persons, ...restOtherPersons } =
      otherPersonsData as any;
    return {
      circumstances,
      statements,
      otherPersons: {
        [OtherPersonsComponentId.PERSONS]: Object.values(persons ?? {}),
        ...restOtherPersons,
      },
    };
  }
);

export const selectUIData = (store: RootState): UIState =>
  store.ocProperty.incident.ui;

export const selectCircumAvailableCities = (store: RootState) =>
  store.ocProperty.incident.ui.circumAvailableCities || [];
